import { BaseTool, IToolContext, IToolManifest } from '../../../core/tools/interfaces/ITool';
import type { ICommand } from '../../../core/commands/ICommand';
import { CompositeCommand } from '../../../core/commands/ICommand';
import { CreateNodeCommand } from '../../../core/commands/CreateNodeCommand';
import { CreateEdgeCommand } from '../../../core/commands/CreateEdgeCommand';
import { getRelationshipMapping, getRelationshipSubtype, getShapeManifest } from '../../../lib/diagram/registries';
import { useDiagramStore } from '../../../stores/diagramStore';
import { getNodeMutationManager } from '../../../core/commands/mutations/NodeMutationManager';
import i18n from '../../../i18n/config';
import { bindGenogramToolName } from './utils/toolNameI18n';

type Direction = 'top' | 'right' | 'bottom' | 'left';

interface ExecuteParamsNew {
  mode: 'new';
  sourceNodeId: string;
  direction: Direction;
  subtypeId: string;
  person: { sex: 'male'|'female'|'unknown'; deceased?: boolean };
}

interface ExecuteParamsLinkExisting {
  mode: 'link-existing';
  sourceNodeId: string;
  direction: Direction;
  subtypeId: string;
}

type ExecuteParams = ExecuteParamsNew | ExecuteParamsLinkExisting;

function anchorsFor(direction: Direction): { from: Direction; to: Direction } {
  switch (direction) {
    case 'top': return { from: 'top', to: 'bottom' };
    case 'right': return { from: 'right', to: 'left' };
    case 'bottom': return { from: 'bottom', to: 'top' };
    case 'left': return { from: 'left', to: 'right' };
  }
}

export class QuickRelateTool extends BaseTool {
  manifest: IToolManifest = {
    id: 'genogram-quick-relate',
    name: i18n.t('genogram:tools.quickRelate', { defaultValue: 'Quick Relate' }) as string,
    icon: '🔗',
    category: 'create',
    description: 'Create relationship to new or existing person',
    version: '1.0.0',
    supportedDiagramTypes: ['genogram' as any],
    requiresSelection: false,
    ui: { toolbar: false, contextMenu: false, propertyPanel: false, sidebar: false, floatingPanel: false, statusBar: false },
    capabilities: {
      modifiesShapes: false,
      createsShapes: true,
      deletesShapes: false,
      modifiesConnectors: false,
      createsConnectors: true,
      deletesConnectors: false,
      requiresUserInput: true,
      supportsMultiSelect: false,
      supportsBatchOperations: true
    }
  };

  constructor() {
    super();
    bindGenogramToolName(this.manifest, 'tools.quickRelate', 'Quick Relate');
  }

  canExecute(_context: IToolContext): boolean {
    return true;
  }

  execute(params?: ExecuteParams): ICommand | ICommand[] | null {
    if (!params) return null;

    if (params.mode === 'link-existing') {
      // For now, emit nothing; canvas should switch to constrained connect mode via event bus if needed.
      // This will be wired in a subsequent step.
      return null;
    }

    const { sourceNodeId, direction: inputDirection, subtypeId, person } = params;
    const { nodes } = useDiagramStore.getState();
    const source = nodes.find((n: any) => n.id === sourceNodeId);
    if (!source) return null;

    // Use manifest default dimensions to align with picker and toolbar defaults
    const personManifest = getShapeManifest('genogram', 'person');
    const defaultDims = personManifest?.defaultDimensions || { width: 100, height: 100 };
    const targetW = defaultDims.width;
    const targetH = defaultDims.height;
    const sourceW = (source.w as number) || (source.width as number) || 60;
    const sourceH = (source.h as number) || (source.height as number) || 60;

    // Determine relationship mapping so we can specialize behavior for children
    const mapping = getRelationshipMapping('genogram' as any, subtypeId);

    // Defaults for general relationships (non-child): place at a fixed gap in the selected direction
    const defaultGap = 50;
    let x = source.x;
    let y = source.y;
    let direction = inputDirection;

    // Special case: child relationships should be placed below the parent with
    // non-overlapping horizontal slots and larger clearance, mirroring AddChildTool.
    if (mapping?.edgeType === 'child') {
      const CHILD_SPACING = 120; // vertical and horizontal clearance between siblings
      // Count existing children for this parent to compute the next slot
      const state = useDiagramStore.getState();
      const existingChildIds = state.edges
        .filter((e: any) => e.type === 'child' && e.from === sourceNodeId)
        .map((e: any) => e.to);

      const childIndex = existingChildIds.length; // 0-based
      const slotStep = Math.ceil(childIndex / 2);
      const slotDir = childIndex % 2 === 1 ? -1 : 1; // odd -> left, even -> right
      const slotSpacing = targetW + CHILD_SPACING;
      const offsetX = childIndex === 0 ? 0 : slotDir * slotStep * slotSpacing;

      const parentCenterX = source.x + sourceW / 2;
      x = Math.round(parentCenterX + offsetX - targetW / 2);
      y = source.y + sourceH + CHILD_SPACING;

      // Force anchors consistent with parent->child relationship and picker side
      direction = 'bottom';
    } else {
      // Generic placement for other relationships
      if (direction === 'top') y = source.y - targetH - defaultGap;
      if (direction === 'bottom') y = source.y + sourceH + defaultGap;
      if (direction === 'left') x = source.x - targetW - defaultGap;
      if (direction === 'right') x = source.x + sourceW + defaultGap;
    }

    const newId = `person-${Date.now()}-${Math.random().toString(36).substr(2,9)}`;
    // Derive a sensible default name from the node definition semantics
    // Keep this logic minimal and local to avoid broader refactors
    const defaultName = (() => {
      if (person.deceased) return i18n.t('genogram:person.deceased', { defaultValue: 'Deceased' }) as string;
      if (person.sex === 'male') return i18n.t('genogram:person.male', { defaultValue: 'Male' }) as string;
      if (person.sex === 'female') return i18n.t('genogram:person.female', { defaultValue: 'Female' }) as string;
      return i18n.t('genogram:person.unknown', { defaultValue: 'Unknown' }) as string;
    })();

    const nodeCmd = new CreateNodeCommand({
      id: newId,
      type: 'person',
      name: defaultName,
      sex: person.sex,
      deceased: person.deceased,
      x, y,
      width: targetW,
      height: targetH,
      w: targetW,
      h: targetH,
      category: 'genogram' as any,
    } as any);

    const { from, to } = anchorsFor(direction);
    // Pre-generate edge id so we can follow-up with an explicit update to ensure flags/label
    const edgeId = `edge-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
    const edgeCmd = new CreateEdgeCommand({
      id: edgeId,
      type: subtypeId,
      from: sourceNodeId,
      to: newId,
      category: 'genogram',
      fromAnchor: from,
      toAnchor: to,
      ydoc: this.context?.doc, // Pass Yjs document for persistence
    });

    // Ensure mapping props are applied and caption makes sense
    const defaults = mapping?.defaultEdgeProps || {};
    const edgeType = mapping?.edgeType || 'line';
    const autoLabelFor = (): string | undefined => {
      if (edgeType === 'marriage') {
        const a = defaults as any;
        if (a.divorcedV2) return i18n.t('diagram:relationships.subtypes.divorce_v2', { defaultValue: 'Divorced' }) as string;
        if (a.divorced) return i18n.t('genogram:relationships.divorce', { defaultValue: 'Divorce' }) as string;
        if (a.separated) return i18n.t('genogram:relationships.separation', { defaultValue: 'Separation' }) as string;
        if (a.engaged) return i18n.t('genogram:relationships.engagement', { defaultValue: 'Engagement' }) as string;
        if (a.house) return i18n.t('genogram:relationships.cohabitation', { defaultValue: 'Cohabitation' }) as string;
        return i18n.t('genogram:relationships.marriage', { defaultValue: 'Marriage' }) as string;
      }
      // For non-marriage, use the translated relationship subtype label
      const sub = getRelationshipSubtype('genogram' as any, subtypeId);
      if (!sub?.label) return undefined;
      const key = sub.label;
      return i18n.t(`diagram:${key}`, { defaultValue: key }) as string;
    };
    const afterCreateUpdate = new (class implements ICommand {
      id = `update-edge-${edgeId}`;
      name = 'Finalize Quick Relate Edge';
      description?: string | undefined;
      timestamp = Date.now();
      async execute() {
        const store = useDiagramStore.getState();
        const e = store.edges.find(ed => ed.id === edgeId);
        if (!e) return;
        // Apply manifest-derived defaults. Child connectors default to hidden captions.
        const isChildEdge = edgeType === 'child';
        const nextAttrs = {
          ...(e as any).attrs,
          ...(defaults as any),
          __autoLabel: !isChildEdge,
          __captionHidden: isChildEdge ? true : false,
          ...(isChildEdge ? { label: undefined } : { label: autoLabelFor() }),
        } as any;
        const mutationManager = getNodeMutationManager();
        mutationManager.updateEdge(edgeId, { type: edgeType as any, attrs: nextAttrs });
        // Ensure local store reflects the latest attrs immediately without triggering permission guards
        useDiagramStore.getState().updateEdge(edgeId, { type: edgeType as any, attrs: nextAttrs }, true);

        // Nudge connectors to recompute after the new node mounts and measures.
        // Some node renderers populate client rects post-mount; bumping an inert flag
        // ensures the EdgeConnector recalculates with the final geometry.
        const bump = () => {
          const edge = useDiagramStore.getState().edges.find(ed => ed.id === edgeId);
          if (!edge) return;
          useDiagramStore.getState().updateEdge(edgeId, { attrs: { ...(edge.attrs || {}), __refreshTick: Date.now() } as any }, true);
        };
        setTimeout(bump, 0);
        setTimeout(bump, 50);
      }
      undo() { /* composite undo handled by outer stack */ }
      redo() { this.execute(); }
    })();

    return new CompositeCommand('Quick Relate', [nodeCmd, edgeCmd, afterCreateUpdate]);
  }
}
